// src/components/event/EventProcurementOverview.jsx

import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/axios";
import { exportToCSV, exportToPDF } from "@/utils/exportUtils";
import { formatCurrency } from "@/utils/formatting";
import { Button } from "@/components/ui/button";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const formatQty = (v) => {
  const n = toNum(v, 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const formatPct = (v) => `${toNum(v, 0).toFixed(1)}%`;

const toneForMoneyVariance = (v) => {
  const n = toNum(v, 0);
  if (n > 0.1) return "text-amber-300 font-semibold";
  if (n < -0.1) return "text-emerald-300 font-semibold";
  return "text-slate-200 font-semibold";
};

const toneForQtyVariance = (v) => {
  const n = toNum(v, 0);
  if (n > 0.1) return "text-rose-300 font-semibold";
  if (n < -0.1) return "text-amber-300 font-semibold";
  return "text-emerald-300 font-semibold";
};

const toneForConfidence = (confidence) => {
  const c = String(confidence || "").toLowerCase();
  if (c === "high") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  if (c === "medium") return "border-cyan-500/30 bg-cyan-500/10 text-cyan-200";
  if (c === "low") return "border-amber-500/30 bg-amber-500/10 text-amber-200";
  return "border-slate-600/40 bg-slate-800/40 text-slate-300";
};

const badgeTone = (active, secondary = false) => {
  if (active && secondary) {
    return "border-cyan-500/30 bg-cyan-500/10 text-cyan-200";
  }
  if (active) {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  }
  return "border-slate-600/40 bg-slate-800/40 text-slate-300";
};

function normalizePayload(payload) {
  return {
    headline: payload?.headline || null,
    items: Array.isArray(payload?.items) ? payload.items : [],
    summary: payload?.summary || null,
    eventName: payload?.event_name || "",
    eventDate: payload?.event_date || "",
    purchaseOrders: Array.isArray(payload?.purchase_orders) ? payload.purchase_orders : [],
    coverage: payload?.coverage || null,
    notes: payload?.notes || null,
  };
}

function pickPrimaryTotals(headline, summary, items, purchaseOrders) {
  if (headline || summary) {
    const forecastCost = toNum(
      headline?.forecast_procurement_cost ?? summary?.total_forecast_cost,
      0
    );

    const orderedCost = toNum(
      headline?.cost_of_purchasing ??
        summary?.cost_of_purchasing ??
        summary?.total_ordered_cost,
      0
    );

    const receivedCost = toNum(
      headline?.cost_of_purchasing_received ??
        summary?.cost_of_purchasing_received ??
        summary?.total_received_cost,
      0
    );

    const variance = toNum(
      headline?.procurement_variance_vs_forecast ??
        summary?.procurement_variance_vs_forecast,
      orderedCost - forecastCost
    );

    const outstandingReceiptCost = toNum(
      headline?.outstanding_receipt_cost ??
        summary?.outstanding_receipt_cost ??
        summary?.total_open_receipt_cost,
      0
    );

    const receiptProgressPct = toNum(
      headline?.receipt_progress_pct ?? summary?.receipt_progress_pct,
      0
    );

    const operationalCompletionPct = toNum(
      headline?.operational_completion_pct ?? summary?.operational_completion_pct,
      receiptProgressPct
    );

    return {
      totalGrossForecastQty: toNum(summary?.total_gross_forecast_qty, 0),
      totalStockOnHandQty: toNum(summary?.total_stock_on_hand_qty, 0),
      stockOnHandApplied: Boolean(summary?.stock_on_hand_applied),

      totalForecastQty: toNum(summary?.total_forecast_qty, 0),
      totalOrderedQty: toNum(summary?.total_ordered_qty, 0),
      totalReceivedQty: toNum(summary?.total_received_qty, 0),
      totalIgnoredQty: toNum(summary?.total_ignored_qty, 0),
      totalRejectedQty: toNum(summary?.total_rejected_qty, 0),
      totalReorderedQty: toNum(summary?.total_reordered_qty, 0),
      totalOpenReceiptQty: toNum(summary?.total_open_receipt_qty, 0),

      totalForecastCost: forecastCost,
      totalOrderedCost: orderedCost,
      totalReceivedCost: receivedCost,
      totalIgnoredCost: toNum(summary?.total_ignored_cost, 0),

      costOfPurchasing: orderedCost,
      receivedPurchasingCost: receivedCost,
      forecastPurchasingCost: forecastCost,

      purchasingVarianceVsForecast: variance,
      outstandingReceiptCost,
      receiptProgressPct,
      operationalCompletionPct,

      purchasedItemsCount: toNum(summary?.purchased_items_count, 0),
      forecastItemsCount: toNum(summary?.forecast_items_count, 0),
      matchedItemsCount: toNum(summary?.matched_items_count, 0),
      purchasesNotInForecastCount: toNum(summary?.purchases_not_in_forecast_count, 0),
      forecastNotPurchasedCount: toNum(summary?.forecast_not_purchased_count, 0),
      poCount: toNum(summary?.po_count, purchaseOrders.length),

      purchasesNotInForecastOrderedCost: toNum(
        summary?.purchases_not_in_forecast_ordered_cost,
        0
      ),
      purchasesNotInForecastReceivedCost: toNum(
        summary?.purchases_not_in_forecast_received_cost,
        0
      ),
      forecastNotPurchasedForecastCost: toNum(
        summary?.forecast_not_purchased_forecast_cost,
        0
      ),

      forecastAvailable: Boolean(summary?.forecast_available),
    };
  }

  const totalGrossForecastQty = items.reduce(
    (s, r) => s + toNum(r.gross_forecasted_qty ?? r.forecasted_qty, 0),
    0
  );
  const totalStockOnHandQty = items.reduce(
    (s, r) => s + toNum(r.stock_on_hand_qty, 0),
    0
  );
  const totalForecastQty = items.reduce((s, r) => s + toNum(r.forecasted_qty, 0), 0);
  const totalOrderedQty = items.reduce((s, r) => s + toNum(r.ordered_qty, 0), 0);
  const totalReceivedQty = items.reduce((s, r) => s + toNum(r.received_qty, 0), 0);
  const totalIgnoredQty = items.reduce((s, r) => s + toNum(r.ignored_qty, 0), 0);
  const totalRejectedQty = items.reduce((s, r) => s + toNum(r.rejected_qty, 0), 0);
  const totalReorderedQty = items.reduce((s, r) => s + toNum(r.reordered_qty, 0), 0);
  const totalOpenReceiptQty = items.reduce(
    (s, r) => s + toNum(r.open_receipt_qty ?? r.outstanding_receipt_qty, 0),
    0
  );

  const totalForecastCost = items.reduce((s, r) => s + toNum(r.forecast_cost, 0), 0);
  const totalOrderedCost = items.reduce((s, r) => s + toNum(r.ordered_cost, 0), 0);
  const totalReceivedCost = items.reduce((s, r) => s + toNum(r.received_cost, 0), 0);
  const totalIgnoredCost = items.reduce((s, r) => s + toNum(r.ignored_cost, 0), 0);

  const purchasedItemsCount = items.filter((r) => r?.in_purchase).length;
  const forecastItemsCount = items.filter((r) => r?.in_forecast).length;
  const matchedItemsCount = items.filter((r) => r?.matched_to_forecast).length;
  const purchasesNotInForecastCount = items.filter((r) => r?.in_purchase && !r?.in_forecast).length;
  const forecastNotPurchasedCount = items.filter((r) => r?.in_forecast && !r?.in_purchase).length;

  const outstandingReceiptCost = items.reduce(
    (s, r) => s + toNum(r.open_receipt_cost ?? r.outstanding_receipt_cost, 0),
    0
  );

  const receiptProgressPct =
    totalOrderedQty > 0 ? (totalReceivedQty / totalOrderedQty) * 100 : 0;

  const operationalCompletionPct =
    totalOrderedQty > 0
      ? ((totalReceivedQty + totalIgnoredQty) / totalOrderedQty) * 100
      : 0;

  return {
    totalGrossForecastQty,
    totalStockOnHandQty,
    stockOnHandApplied: items.some((r) => toNum(r.stock_on_hand_qty, 0) > 0),

    totalForecastQty,
    totalOrderedQty,
    totalReceivedQty,
    totalIgnoredQty,
    totalRejectedQty,
    totalReorderedQty,
    totalOpenReceiptQty,

    totalForecastCost,
    totalOrderedCost,
    totalReceivedCost,
    totalIgnoredCost,

    costOfPurchasing: totalOrderedCost,
    receivedPurchasingCost: totalReceivedCost,
    forecastPurchasingCost: totalForecastCost,

    purchasingVarianceVsForecast: totalOrderedCost - totalForecastCost,
    outstandingReceiptCost,
    receiptProgressPct,
    operationalCompletionPct,

    purchasedItemsCount,
    forecastItemsCount,
    matchedItemsCount,
    purchasesNotInForecastCount,
    forecastNotPurchasedCount,
    poCount: purchaseOrders.length,

    purchasesNotInForecastOrderedCost: items
      .filter((r) => r?.in_purchase && !r?.in_forecast)
      .reduce((s, r) => s + toNum(r.ordered_cost, 0), 0),

    purchasesNotInForecastReceivedCost: items
      .filter((r) => r?.in_purchase && !r?.in_forecast)
      .reduce((s, r) => s + toNum(r.received_cost, 0), 0),

    forecastNotPurchasedForecastCost: items
      .filter((r) => r?.in_forecast && !r?.in_purchase)
      .reduce((s, r) => s + toNum(r.forecast_cost, 0), 0),

    forecastAvailable: items.some((r) => toNum(r.forecasted_qty, 0) > 0),
  };
}

const EventProcurementOverview = ({ eventId }) => {
  const { canViewEventProcurement } = useUserPermissions();
  const componentRef = useRef(null);

  const [headline, setHeadline] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!eventId || !canViewEventProcurement) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(`/event/${eventId}/procurement`);
        if (cancelled) return;

        const payload = normalizePayload(res?.data);

        setHeadline(payload.headline);
        setItems(payload.items);
        setSummary(payload.summary);
        setEventName(payload.eventName);
        setEventDate(payload.eventDate);
        setPurchaseOrders(payload.purchaseOrders);
        setCoverage(payload.coverage);
        setNotes(payload.notes);
      } catch (err) {
        if (cancelled) return;

        console.error("Failed to load procurement overview:", err);

        setHeadline(null);
        setItems([]);
        setSummary(null);
        setEventName("");
        setEventDate("");
        setPurchaseOrders([]);
        setCoverage(null);
        setNotes(null);
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to load procurement overview"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [eventId, canViewEventProcurement]);

  const totals = useMemo(() => {
    return pickPrimaryTotals(headline, summary, items, purchaseOrders);
  }, [headline, summary, items, purchaseOrders]);

  const exportRows = useMemo(() => {
    return items.map((row) => ({
      ItemCode: row.item_code || "",
      ItemName: row.item_name || "",
      Unit: row.unit || "",
      InForecast: row.in_forecast ? "Yes" : "No",
      InPurchase: row.in_purchase ? "Yes" : "No",
      MatchedToForecast: row.matched_to_forecast ? "Yes" : "No",
      MatchBasis: row.item_match_basis || "",
      MatchConfidence: row.item_match_confidence || "",

      GrossForecastedQty: toNum(row.gross_forecasted_qty ?? row.forecasted_qty, 0),
      StockOnHandQty: toNum(row.stock_on_hand_qty, 0),
      NetForecastedQty: toNum(row.forecasted_qty, 0),

      OrderedQty: toNum(row.ordered_qty, 0),
      ReceivedQty: toNum(row.received_qty, 0),
      IgnoredQty: toNum(row.ignored_qty, 0),
      RejectedQty: toNum(row.rejected_qty, 0),
      ReorderedQty: toNum(row.reordered_qty, 0),
      ReceiptProgressPct: toNum(row.receipt_progress_pct, 0),

      MissingQtyVsForecast: toNum(row.missing_qty_vs_forecast, 0),
      GrossMissingQtyVsForecast: toNum(row.gross_missing_qty_vs_forecast, 0),

      OutstandingReceiptQty: toNum(row.outstanding_receipt_qty, 0),
      OpenReceiptQty: toNum(row.open_receipt_qty ?? row.outstanding_receipt_qty, 0),

      VarianceOrderedVsForecast: toNum(row.variance_ordered_vs_forecast, 0),
      VarianceOrderedVsGrossForecast: toNum(row.variance_ordered_vs_gross_forecast, 0),

      StockUnitCost: toNum(row.stock_unit_cost, 0),
      POUnitPrice: toNum(row.ordered_unit_price, 0),
      EffectiveUnitCost: toNum(row.effective_unit_cost, 0),

      ForecastCost: toNum(row.forecast_cost, 0),
      OrderedCost: toNum(row.ordered_cost, 0),
      ReceivedCost: toNum(row.received_cost, 0),
      IgnoredCost: toNum(row.ignored_cost, 0),
      OutstandingReceiptCost: toNum(row.outstanding_receipt_cost, 0),

      PONumbers: Array.isArray(row.po_numbers) ? row.po_numbers.join(", ") : "",
      POStatuses: Array.isArray(row.po_statuses) ? row.po_statuses.join(", ") : "",
    }));
  }, [items]);

  const coverageCounts = useMemo(() => {
    const matched = Array.isArray(coverage?.matched_items)
      ? coverage.matched_items.length
      : totals.matchedItemsCount;

    const purchasesOutside = Array.isArray(coverage?.purchases_not_in_forecast)
      ? coverage.purchases_not_in_forecast.length
      : totals.purchasesNotInForecastCount;

    const forecastMissing = Array.isArray(coverage?.forecast_not_purchased)
      ? coverage.forecast_not_purchased.length
      : totals.forecastNotPurchasedCount;

    return {
      matched,
      purchasesOutside,
      forecastMissing,
    };
  }, [coverage, totals]);

  if (!canViewEventProcurement) {
    return (
      <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-200 font-semibold">
        You do not have permission to view this procurement overview.
      </div>
    );
  }

  return (
    <div
      ref={componentRef}
      className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur p-5 space-y-5 shadow-xl"
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Event Procurement Overview</h2>
          <p className="text-sm text-slate-400 mt-1">
            Event-linked purchasing, receiving, and forecast comparison.
          </p>

          {eventName ? (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-cyan-300">
                Event: <span className="font-semibold">{eventName}</span>
              </p>
              {eventDate ? (
                <p className="text-xs text-slate-400">
                  Date: <span className="text-slate-300">{String(eventDate).slice(0, 10)}</span>
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => exportToCSV(exportRows, `Event_Procurement_${eventId}.csv`)}
            disabled={!items.length}
          >
            Export CSV
          </Button>
          <Button
            type="button"
            onClick={() => exportToPDF(componentRef, `Event_Procurement_${eventId}.pdf`)}
            disabled={!items.length}
          >
            Export PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-slate-700/60 bg-slate-950/50 p-5 text-slate-300">
          Loading procurement overview...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-5 text-rose-200">
          {error}
        </div>
      ) : (
        <>
          {totals.stockOnHandApplied ? (
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-sm text-cyan-100">
              <div className="font-semibold">Stock-aware forecast is active</div>
              <div className="mt-1 text-cyan-200/80">
                Gross forecast demand is being reduced by available stock on hand before forecast purchasing cost is calculated.
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard
              title="Cost of Purchasing"
              value={formatCurrency(totals.costOfPurchasing)}
              subtitle="Primary ordered procurement cost for this event"
            />

            <MetricCard
              title="Received Purchasing Cost"
              value={formatCurrency(totals.receivedPurchasingCost)}
              subtitle="Cost represented by received goods"
            />

            <MetricCard
              title="Forecast Purchasing Cost"
              value={formatCurrency(totals.forecastPurchasingCost)}
              subtitle={
                totals.forecastAvailable
                  ? totals.stockOnHandApplied
                    ? "Net forecast cost after stock-on-hand deduction"
                    : "Forecast cost from menu-linked requirement"
                  : "No forecast available"
              }
            />

            <MetricCard
              title="Outstanding Receipt Cost"
              value={formatCurrency(totals.outstandingReceiptCost)}
              subtitle="Cost still operationally open after receipts and ignores"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard
              title="Purchasing Variance"
              value={formatCurrency(totals.purchasingVarianceVsForecast)}
              subtitle="Cost of purchasing minus forecast purchasing cost"
              toneClass={toneForMoneyVariance(totals.purchasingVarianceVsForecast)}
            />

            <MetricCard
              title="Receipt Progress"
              value={formatPct(totals.receiptProgressPct)}
              subtitle="Received quantity against ordered quantity"
            />

            <MetricCard
              title="Operational Completion"
              value={formatPct(totals.operationalCompletionPct)}
              subtitle="Received + ignored quantity against ordered quantity"
            />

            <MetricCard
              title="Purchase Orders Linked"
              value={String(totals.poCount)}
              subtitle="Event-linked purchase orders"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Gross Forecast Qty"
              value={formatQty(totals.totalGrossForecastQty)}
              subtitle="Total event requirement before stock deduction"
            />

            <MetricCard
              title="Stock On Hand Qty"
              value={formatQty(totals.totalStockOnHandQty)}
              subtitle="Current stock applied against event demand"
            />

            <MetricCard
              title="Net Forecast Qty"
              value={formatQty(totals.totalForecastQty)}
              subtitle="Remaining requirement after stock deduction"
            />

            <MetricCard
              title="Purchased Items"
              value={String(totals.purchasedItemsCount)}
              subtitle="All procurement rows on this event"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Ignored Qty"
              value={formatQty(totals.totalIgnoredQty)}
              subtitle="Quantity closed by ignore action"
            />

            <MetricCard
              title="Rejected Qty"
              value={formatQty(totals.totalRejectedQty)}
              subtitle="Total rejected quantity recorded"
            />

            <MetricCard
              title="Reordered Qty"
              value={formatQty(totals.totalReorderedQty)}
              subtitle="Quantity marked for reorder/backorder"
            />

            <MetricCard
              title="Open Receipt Qty"
              value={formatQty(totals.totalOpenReceiptQty)}
              subtitle="Still operationally open after receipts and ignores"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="text-xs text-emerald-200/80">Matched to Forecast</div>
              <div className="text-2xl font-bold text-emerald-100 mt-1">
                {coverageCounts.matched}
              </div>
              <div className="text-xs text-emerald-200/70 mt-1">
                Purchases that also exist in the menu forecast
              </div>
            </div>

            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
              <div className="text-xs text-cyan-200/80">Purchases Not In Forecast</div>
              <div className="text-2xl font-bold text-cyan-100 mt-1">
                {coverageCounts.purchasesOutside}
              </div>
              <div className="text-xs text-cyan-200/70 mt-1">
                Cost: {formatCurrency(totals.purchasesNotInForecastOrderedCost)}
              </div>
            </div>

            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="text-xs text-amber-200/80">Forecast Not Yet Purchased</div>
              <div className="text-2xl font-bold text-amber-100 mt-1">
                {coverageCounts.forecastMissing}
              </div>
              <div className="text-xs text-amber-200/70 mt-1">
                Forecast cost: {formatCurrency(totals.forecastNotPurchasedForecastCost)}
              </div>
            </div>
          </div>

          {purchaseOrders.length > 0 ? (
            <div className="rounded-2xl border border-slate-700/60 overflow-hidden">
              <div className="bg-slate-800/90 px-4 py-3 border-b border-slate-700/60">
                <h3 className="text-sm font-semibold text-white">Linked Purchase Orders</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full w-full text-sm text-slate-200">
                  <thead className="bg-slate-900/70 text-slate-300">
                    <tr>
                      <th className="p-3 text-left">PO Number</th>
                      <th className="p-3 text-left">Status</th>
                      <th className="p-3 text-left">Created</th>
                      <th className="p-3 text-left">Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.map((po, idx) => (
                      <tr
                        key={`${po.po_id || po.po_number || "po"}-${idx}`}
                        className="border-t border-slate-800 bg-slate-950/30"
                      >
                        <td className="p-3 text-white font-medium">
                          {po.po_number || `PO #${po.po_id}`}
                        </td>
                        <td className="p-3 text-slate-300">{po.status || "—"}</td>
                        <td className="p-3 text-slate-400">
                          {po.created_at ? String(po.created_at).slice(0, 10) : "—"}
                        </td>
                        <td className="p-3 text-slate-400">
                          {po.approved_at ? String(po.approved_at).slice(0, 10) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-slate-700/60">
            <table className="min-w-[2850px] w-full text-sm text-slate-200">
              <thead className="bg-slate-800/90 text-slate-200">
                <tr>
                  <th className="p-3 text-left">Item</th>
                  <th className="p-3 text-left">Code</th>
                  <th className="p-3 text-center">Unit</th>
                  <th className="p-3 text-center">Coverage</th>
                  <th className="p-3 text-center">Match Confidence</th>
                  <th className="p-3 text-right">Gross Forecast Qty</th>
                  <th className="p-3 text-right">Stock On Hand</th>
                  <th className="p-3 text-right">Net Forecast Qty</th>
                  <th className="p-3 text-right">Ordered Qty</th>
                  <th className="p-3 text-right">Received Qty</th>
                  <th className="p-3 text-right">Ignored Qty</th>
                  <th className="p-3 text-right">Rejected Qty</th>
                  <th className="p-3 text-right">Reordered Qty</th>
                  <th className="p-3 text-right">Receipt %</th>
                  <th className="p-3 text-right">Open Receipt Qty</th>
                  <th className="p-3 text-right">Missing vs Net Forecast</th>
                  <th className="p-3 text-right">Ordered vs Net Forecast</th>
                  <th className="p-3 text-right">Stock Unit Cost</th>
                  <th className="p-3 text-right">PO Unit Price</th>
                  <th className="p-3 text-right">Effective Unit Cost</th>
                  <th className="p-3 text-right">Forecast Cost</th>
                  <th className="p-3 text-right">Ordered Cost</th>
                  <th className="p-3 text-right">Received Cost</th>
                  <th className="p-3 text-right">Ignored Cost</th>
                  <th className="p-3 text-right">Open Receipt Cost</th>
                  <th className="p-3 text-left">PO Numbers</th>
                </tr>
              </thead>

              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={26} className="p-6 text-center text-slate-400 bg-slate-950/40">
                      No procurement data found for this event.
                    </td>
                  </tr>
                ) : (
                  items.map((row, idx) => (
                    <tr
                      key={`${row.item_id || row.item_code || row.item_name || "row"}-${idx}`}
                      className="border-t border-slate-800 bg-slate-950/30 hover:bg-slate-900/40 transition"
                    >
                      <td className="p-3 text-left font-medium text-white">
                        {row.item_name || "—"}
                      </td>

                      <td className="p-3 text-left text-slate-300">{row.item_code || "—"}</td>

                      <td className="p-3 text-center text-slate-300">{row.unit || "—"}</td>

                      <td className="p-3 text-center">
                        <div className="flex flex-wrap items-center justify-center gap-1">
                          <span
                            className={`px-2 py-1 rounded-full text-[10px] border ${badgeTone(
                              row.in_forecast
                            )}`}
                          >
                            Forecast
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-[10px] border ${badgeTone(
                              row.in_purchase,
                              true
                            )}`}
                          >
                            Purchase
                          </span>
                        </div>
                      </td>

                      <td className="p-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`px-2 py-1 rounded-full text-[10px] border ${toneForConfidence(
                              row.item_match_confidence
                            )}`}
                          >
                            {row.item_match_confidence || "none"}
                          </span>
                          <span className="text-[10px] text-slate-500">
                            {row.item_match_basis || "—"}
                          </span>
                        </div>
                      </td>

                      <td className="p-3 text-right">{formatQty(row.gross_forecasted_qty ?? row.forecasted_qty)}</td>
                      <td className={`p-3 text-right ${toneForQtyVariance(-(toNum(row.stock_on_hand_qty, 0)))}`}>
                        {formatQty(row.stock_on_hand_qty)}
                      </td>
                      <td className="p-3 text-right">{formatQty(row.forecasted_qty)}</td>
                      <td className="p-3 text-right">{formatQty(row.ordered_qty)}</td>
                      <td className="p-3 text-right">{formatQty(row.received_qty)}</td>
                      <td className="p-3 text-right">{formatQty(row.ignored_qty)}</td>
                      <td className="p-3 text-right">{formatQty(row.rejected_qty)}</td>
                      <td className="p-3 text-right">{formatQty(row.reordered_qty)}</td>
                      <td className="p-3 text-right">{formatPct(row.receipt_progress_pct)}</td>

                      <td className={`p-3 text-right ${toneForQtyVariance(row.open_receipt_qty ?? row.outstanding_receipt_qty)}`}>
                        {formatQty(row.open_receipt_qty ?? row.outstanding_receipt_qty)}
                      </td>

                      <td className={`p-3 text-right ${toneForQtyVariance(row.missing_qty_vs_forecast)}`}>
                        {formatQty(row.missing_qty_vs_forecast)}
                      </td>

                      <td className={`p-3 text-right ${toneForQtyVariance(row.variance_ordered_vs_forecast)}`}>
                        {formatQty(row.variance_ordered_vs_forecast)}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.stock_unit_cost, 0))}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.ordered_unit_price, 0))}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.effective_unit_cost, 0))}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.forecast_cost, 0))}
                      </td>

                      <td className="p-3 text-right font-semibold text-white">
                        {formatCurrency(toNum(row.ordered_cost, 0))}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.received_cost, 0))}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.ignored_cost, 0))}
                      </td>

                      <td className="p-3 text-right">
                        {formatCurrency(toNum(row.open_receipt_cost ?? row.outstanding_receipt_cost, 0))}
                      </td>

                      <td className="p-3 text-left text-slate-300">
                        {Array.isArray(row.po_numbers) && row.po_numbers.length
                          ? row.po_numbers.join(", ")
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-slate-700/60 bg-slate-950/50 p-4 text-xs text-slate-400">
            <div className="font-semibold text-slate-300">Notes</div>

            <div className="mt-2 space-y-1">
              <div>• Cost of Purchasing = total ordered procurement cost for this event</div>
              <div>• Received Purchasing Cost = cost represented by goods already received</div>
              <div>• Forecast Purchasing Cost = remaining forecast purchasing need after stock-aware deduction when available</div>
              <div>• Gross Forecast Qty = full event requirement before current stock is applied</div>
              <div>• Stock On Hand Qty = stock already available to cover that requirement</div>
              <div>• Net Forecast Qty = Gross Forecast Qty − Stock On Hand Qty</div>
              <div>• Open Receipt Qty = Ordered Qty − Received Qty − Ignored Qty</div>
              <div>• Outstanding Receipt Cost = cost still operationally open after receipts and ignores</div>
              <div>• Missing vs Net Forecast = Net Forecast Qty − Ordered Qty</div>
              <div>• Ordered vs Net Forecast = Ordered Qty − Net Forecast Qty</div>
              <div>• Effective Unit Cost = PO Unit Price when present, otherwise Stock Unit Cost</div>
              <div>• Match confidence shows how safely forecast and purchase rows were linked</div>
              <div>• This view includes all event-linked PO purchases, not only menu-linked stock items</div>

              {notes?.classification ? <div>• {notes.classification}</div> : null}
              {notes?.matching_logic ? <div>• {notes.matching_logic}</div> : null}
              {notes?.forecast_behavior ? <div>• {notes.forecast_behavior}</div> : null}
              {notes?.pricing_behavior ? <div>• {notes.pricing_behavior}</div> : null}
              {notes?.receiving_behavior ? <div>• {notes.receiving_behavior}</div> : null}
              {notes?.stock_check_behavior ? <div>• {notes.stock_check_behavior}</div> : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function MetricCard({ title, value, subtitle, toneClass = "text-white" }) {
  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-950/50 p-4">
      <div className="text-xs text-slate-400">{title}</div>
      <div className={`text-xl font-bold mt-1 ${toneClass}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
    </div>
  );
}

export default EventProcurementOverview;